import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import axios from "axios";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { 
  ArrowLeft, 
  Calendar,
  Store,
  ShoppingCart,
  Package,
  DollarSign,
  RotateCcw,
  ChevronDown,
  ChevronUp,
  FileSpreadsheet
} from "lucide-react";

const API = "/api";

export default function GunlukSatisRaporuPage() {
  const { token } = useAuth();
  const [loading, setLoading] = useState(false);
  const [baslangicTarihi, setBaslangicTarihi] = useState(() => {
    // Varsayılan olarak son 7 gün
    const date = new Date();
    date.setDate(date.getDate() - 7);
    return date.toISOString().split("T")[0];
  });
  const [bitisTarihi, setBitisTarihi] = useState(() => {
    return new Date().toISOString().split("T")[0];
  });
  
  const [rapor, setRapor] = useState(null);
  const [expandedDays, setExpandedDays] = useState([]);

  const fetchRapor = async () => {
    // Basit tarih kontrolü
    const start = new Date(baslangicTarihi);
    const end = new Date(bitisTarihi);
    if (start > end) {
      toast.error("Başlangıç tarihi bitiş tarihinden sonra olamaz!");
      return;
    }
    
    setLoading(true);
    try {
      const response = await axios.get(
        `${API}/nebim/rapor/gunluk-satis?token=${token}&baslangic=${baslangicTarihi}&bitis=${bitisTarihi}`
      );
      if (response.data.success) {
        setRapor(response.data.rapor);
        // İlk günü otomatik aç
        if (response.data.rapor?.gunler?.length > 0) {
          setExpandedDays([response.data.rapor.gunler[0].tarih]);
        }
        if (response.data.rapor?.gunler?.length === 0) {
          toast.info("Seçilen tarih aralığında satış verisi bulunamadı");
        }
      } else {
        toast.error(response.data.error || "Rapor alınamadı");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || error.response?.data?.error || "Rapor hatası");
    } finally {
      setLoading(false);
    }
  };

  const toggleDay = (tarih) => {
    setExpandedDays(prev => 
      prev.includes(tarih) 
        ? prev.filter(d => d !== tarih)
        : [...prev, tarih]
    );
  };

  const exportExcel = () => {
    toast.info("Excel export özelliği yakında...");
  };

  return (
    <div className="space-y-6 fade-in" data-testid="gunluk-satis-raporu-page">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => window.history.back()}>
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Günlük Satış Raporu</h1>
          <p className="text-slate-500 text-sm">Günlük satış özeti ve mağaza performansı</p>
        </div>
      </div>

      {/* Filtreler */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-wrap gap-4 items-end">
            <div className="space-y-2">
              <Label>Başlangıç Tarihi</Label>
              <Input
                type="date"
                value={baslangicTarihi}
                onChange={(e) => setBaslangicTarihi(e.target.value)}
                data-testid="input-baslangic"
              />
            </div>
            <div className="space-y-2">
              <Label>Bitiş Tarihi</Label>
              <Input
                type="date"
                value={bitisTarihi}
                onChange={(e) => setBitisTarihi(e.target.value)}
                data-testid="input-bitis"
              />
            </div>
            <Button onClick={fetchRapor} disabled={loading} className="bg-blue-600 hover:bg-blue-700">
              {loading ? "Yükleniyor..." : "Rapor Getir"}
            </Button>
            <Button variant="outline" onClick={exportExcel}>
              <FileSpreadsheet className="w-4 h-4 mr-2" />
              Excel'e Aktar
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Özet Kartları */}
      {rapor && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            <Card>
              <CardContent className="pt-4">
                <div className="flex items-center gap-2 text-slate-500 text-sm mb-1">
                  <Store className="w-4 h-4" />
                  Toplam Mağaza
                </div>
                <p className="text-2xl font-bold">{rapor.ozet?.toplamMagaza || 0}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4">
                <div className="flex items-center gap-2 text-slate-500 text-sm mb-1">
                  <ShoppingCart className="w-4 h-4" />
                  Toplam Satış Adedi
                </div>
                <p className="text-2xl font-bold">{rapor.ozet?.toplamSatisAdedi || 0}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4">
                <div className="flex items-center gap-2 text-slate-500 text-sm mb-1">
                  <Package className="w-4 h-4" />
                  Toplam Miktar
                </div>
                <p className="text-2xl font-bold">{rapor.ozet?.toplamMiktar || 0}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4">
                <div className="flex items-center gap-2 text-slate-500 text-sm mb-1">
                  <DollarSign className="w-4 h-4" />
                  Toplam Tutar
                </div>
                <p className="text-2xl font-bold text-emerald-600">
                  {(rapor.ozet?.toplamTutar || 0).toLocaleString("tr-TR")} ₺
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4">
                <div className="flex items-center gap-2 text-slate-500 text-sm mb-1">
                  <RotateCcw className="w-4 h-4" />
                  İade Miktarı
                </div>
                <p className="text-2xl font-bold text-red-500">{rapor.ozet?.iadeMiktari || 0}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4">
                <div className="flex items-center gap-2 text-slate-500 text-sm mb-1">
                  <RotateCcw className="w-4 h-4" />
                  İade Tutarı
                </div>
                <p className="text-2xl font-bold text-red-500">
                  {(rapor.ozet?.iadeTutari || 0).toLocaleString("tr-TR")} ₺
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Gün Bazlı Liste */}
          <div className="space-y-4">
            {rapor.gunler?.map((gun, idx) => (
              <Card key={idx}>
                <CardContent className="p-0">
                  {/* Gün Header */}
                  <div 
                    className="flex items-center justify-between p-4 cursor-pointer hover:bg-slate-50"
                    onClick={() => toggleDay(gun.tarih)}
                  >
                    <div className="flex items-center gap-4">
                      <Calendar className="w-5 h-5 text-blue-500" />
                      <div>
                        <p className="font-semibold">{gun.tarih}</p>
                        <p className="text-sm text-slate-500">{gun.magazaSayisi} mağaza</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-6">
                      <div className="text-right">
                        <p className="text-sm text-slate-500">Satış</p>
                        <p className="font-medium">{gun.satisAdedi}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm text-slate-500">Adet</p>
                        <p className="font-medium">{gun.miktar}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm text-slate-500">Tutar</p>
                        <p className="font-medium text-emerald-600">{gun.tutar?.toLocaleString("tr-TR")} ₺</p>
                      </div>
                      {expandedDays.includes(gun.tarih) ? (
                        <ChevronUp className="w-5 h-5 text-slate-400" />
                      ) : (
                        <ChevronDown className="w-5 h-5 text-slate-400" />
                      )}
                    </div>
                  </div>

                  {/* Gün Detayları */}
                  {expandedDays.includes(gun.tarih) && (
                    <div className="border-t">
                      <table className="w-full">
                        <thead className="bg-slate-50">
                          <tr>
                            <th className="text-left p-3 text-sm font-medium text-slate-600">Mağaza</th>
                            <th className="text-right p-3 text-sm font-medium text-slate-600">Satış Adedi</th>
                            <th className="text-right p-3 text-sm font-medium text-slate-600">Miktar</th>
                            <th className="text-right p-3 text-sm font-medium text-slate-600">Tutar</th>
                            <th className="text-right p-3 text-sm font-medium text-slate-600">İade Miktar</th>
                            <th className="text-right p-3 text-sm font-medium text-slate-600">İade Tutar</th>
                          </tr>
                        </thead>
                        <tbody>
                          {gun.magazalar?.map((magaza, mIdx) => (
                            <tr key={mIdx} className="border-t hover:bg-slate-50">
                              <td className="p-3 font-medium">{magaza.ad}</td>
                              <td className="p-3 text-right">{magaza.satisAdedi}</td>
                              <td className="p-3 text-right">{magaza.miktar}</td>
                              <td className="p-3 text-right text-emerald-600">{magaza.tutar?.toLocaleString("tr-TR")} ₺</td>
                              <td className="p-3 text-right text-red-500">{magaza.iadeMiktar || 0}</td>
                              <td className="p-3 text-right text-red-500">{(magaza.iadeTutar || 0).toLocaleString("tr-TR")} ₺</td>
                            </tr>
                          ))}
                          <tr className="bg-slate-100 font-semibold">
                            <td className="p-3">Gün Toplamı</td>
                            <td className="p-3 text-right">{gun.satisAdedi}</td>
                            <td className="p-3 text-right">{gun.miktar}</td>
                            <td className="p-3 text-right text-emerald-600">{gun.tutar?.toLocaleString("tr-TR")} ₺</td>
                            <td className="p-3 text-right text-red-500">{gun.iadeMiktar || 0}</td>
                            <td className="p-3 text-right text-red-500">{(gun.iadeTutar || 0).toLocaleString("tr-TR")} ₺</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        </>
      )}

      {!rapor && !loading && (
        <div className="text-center py-12">
          <Calendar className="w-16 h-16 mx-auto text-slate-300 mb-4" />
          <p className="text-slate-500">Tarih aralığı seçip "Rapor Getir" butonuna tıklayın</p>
        </div>
      )}
    </div>
  );
}
